import { Box, Text, VStack } from '@chakra-ui/react';
import defaultPalette from '@olo-web/theme/colors';
import { motion } from 'framer-motion';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const CIRCLE_FILL_LIGHT_BLUE = defaultPalette.primary[100];
const BLACK = defaultPalette.black;

const DISPLAY_MS = 3000;

export type TablePayConfirmationPayload = {
  amountPaidLabel: string;
  tipLabel: string;
  showTipLine: boolean;
  onDuringDisplay?: () => void | Promise<unknown>;
};

type Pending = TablePayConfirmationPayload & {
  resolve: () => void;
  reject: (reason?: unknown) => void;
};

type TablePayConfirmationContextValue = {
  present: (payload: TablePayConfirmationPayload) => Promise<void>;
};

const TablePayConfirmationContext = createContext<TablePayConfirmationContextValue | null>(null);

export const useTablePayConfirmationOptional = () => useContext(TablePayConfirmationContext);

const CHECK_PATH_REPEAT_DELAY_S = 1.25;

const checkVariants = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: {
        duration: 0.35,
        delay: 0.35,
        ease: 'easeOut',
        repeat: Infinity,
        repeatDelay: CHECK_PATH_REPEAT_DELAY_S,
        repeatType: 'loop',
      },
      opacity: { duration: 0.15, delay: 0.35 },
    },
  },
};

const TablePayConfirmationOverlay = ({
  pending,
  onDismiss,
}: {
  pending: Pending;
  onDismiss: () => void;
}) => {
  useEffect(() => {
    let cancelled = false;

    const minDisplay = new Promise<void>((r) => window.setTimeout(r, DISPLAY_MS));

    const work = (async () => {
      if (cancelled) return;
      await Promise.resolve(pending.onDuringDisplay?.());
    })();

    Promise.all([minDisplay, work])
      .then(() => {
        if (cancelled) return;
        pending.resolve();
        onDismiss();
      })
      .catch((err) => {
        if (cancelled) return;
        pending.reject(err);
        onDismiss();
      });

    return () => {
      cancelled = true;
    };
  }, [pending, onDismiss]);

  return (
    <Box
      role="status"
      aria-live="polite"
      position="fixed"
      inset={0}
      zIndex={10000001}
      minH="100vh"
      minW="100vw"
      w="100vw"
      h="100vh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      bg="white"
      px={{ base: 6, md: 10 }}
      py={8}
      sx={{
        '@keyframes table-pay-confirm-fade': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        animation: 'table-pay-confirm-fade 0.25s ease-out',
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        style={{ width: '100%', maxWidth: '100%' }}
      >
        <VStack
          spacing={{ base: 6, md: 8 }}
          textAlign="center"
          w="100%"
          maxW="100%"
          px={{ base: 2, md: 4 }}
        >
          <Box
            position="relative"
            w={{ base: '96px', md: '112px' }}
            h={{ base: '96px', md: '112px' }}
            mx="auto"
          >
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 88 88"
              aria-hidden
              style={{ display: 'block' }}
            >
              <motion.g
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                style={{ transformOrigin: '44px 44px' }}
              >
                <circle cx="44" cy="44" r="38" fill={CIRCLE_FILL_LIGHT_BLUE} />
              </motion.g>
              <motion.path
                d="M26 46 L38 58 L62 32"
                fill="none"
                stroke={BLACK}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                variants={checkVariants}
                initial="hidden"
                animate="visible"
              />
            </svg>
          </Box>
          <VStack spacing={{ base: 8, md: 10 }} w="100%">
            <Text
              textStyle="h3"
              as="h2"
              fontWeight="600"
              color={BLACK}
              fontSize={{ base: '3xl', md: '4xl' }}
            >
              Payment confirmed
            </Text>
            <VStack spacing={1} w="100%">
              <Text
                fontSize={{ base: '3xl', md: '4xl' }}
                fontWeight="700"
                color={BLACK}
                letterSpacing="tight"
              >
                {pending.amountPaidLabel}
              </Text>
              {pending.showTipLine ? (
                <Text
                  fontSize={{ base: 'lg', md: 'xl' }}
                  color={BLACK}
                  fontWeight="700"
                  lineHeight="short"
                >
                  Including {pending.tipLabel} tip
                </Text>
              ) : null}
            </VStack>
          </VStack>
        </VStack>
      </motion.div>
    </Box>
  );
};

export const TablePayConfirmationProvider = ({ children }: { children: React.ReactNode }) => {
  const [pending, setPending] = useState<Pending | null>(null);

  const present = useCallback((payload: TablePayConfirmationPayload) => {
    return new Promise<void>((resolve, reject) => {
      setPending({ ...payload, resolve, reject });
    });
  }, []);

  const onDismiss = useCallback(() => {
    setPending(null);
  }, []);

  const value = useMemo(() => ({ present }), [present]);

  return (
    <TablePayConfirmationContext.Provider value={value}>
      {children}
      {pending ? <TablePayConfirmationOverlay pending={pending} onDismiss={onDismiss} /> : null}
    </TablePayConfirmationContext.Provider>
  );
};
